import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Copy, Mail, RefreshCw, UserPlus, ShieldCheck } from "lucide-react";

const STATUS_BADGE = {
  pending: { label: "En attente", className: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" },
  used: { label: "Acceptée", className: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]" },
  expired: { label: "Expirée", className: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]" },
};

const ROLE_BADGE = {
  licencie: { label: "Licencié", className: "bg-[#EEF2FF] text-[#3730A3] border-[#C7D2FE]" },
  admin: { label: "Admin", className: "bg-[#FCE7F3] text-[#9D174D] border-[#FBCFE8]" },
  commercant: { label: "Commerçant", className: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]" },
};

const ROLE_FILTERS = [
  { value: "all", label: "Tous" },
  { value: "licencie", label: "Licenciés" },
  { value: "admin", label: "Admins" },
  { value: "commercant", label: "Commerçants" },
];

export function InvitationsPanel() {
  const [invitations, setInvitations] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [role, setRole] = useState("licencie"); // form role

  React.useEffect(() => {
    const handler = () => setRole("admin");
    window.addEventListener("pde:invite-admin", handler);
    return () => window.removeEventListener("pde:invite-admin", handler);
  }, []);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ email: "", nom: "", prenom: "", zone: "", responsable: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [lastDevLink, setLastDevLink] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [zoneCustom, setZoneCustom] = useState(false);

  useEffect(() => {
    api.get("/agencies").then(({ data }) => setAgencies(data || [])).catch(() => {});
  }, []);

  const reload = useCallback(async () => {
    setListLoading(true);
    try {
      const { data } = await api.get("/invitations");
      setInvitations(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        email: form.email.trim(),
        nom: form.nom.trim(),
        prenom: form.prenom.trim(),
        role,
      };
      if (role === "licencie") {
        payload.zone = form.zone.trim();
        payload.responsable = form.responsable.trim();
      }
      const { data } = await api.post("/invitations", payload);
      toast.success(`Invitation ${role === "admin" ? "admin" : role} créée pour ${data.email}`);
      if (data.dev_link) setLastDevLink(data.dev_link);
      setForm({ email: "", nom: "", prenom: "", zone: "", responsable: "" });
      reload();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success("Lien copié"); }
    catch { toast.error("Impossible de copier — copiez manuellement"); }
  };

  const visibleInvitations = filter === "all" ? invitations : invitations.filter((i) => i.role === filter);
  const counts = {
    admin: invitations.filter((i) => i.role === "admin").length,
    licencie: invitations.filter((i) => i.role === "licencie").length,
    commercant: invitations.filter((i) => i.role === "commercant").length,
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <section className="lg:col-span-2">
        <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(57,125,255,0.06)] p-6 sm:p-7" data-testid="invite-panel">
          <div className="flex items-center gap-2 mb-1">
            {role === "admin" ? <ShieldCheck className="w-5 h-5 text-[#397DFF]" /> : <UserPlus className="w-5 h-5 text-[#397DFF]" />}
            <h2 className="font-display text-2xl font-bold text-[#191D1E]">
              Inviter en tant que…
            </h2>
          </div>
          <p className="text-sm text-[#939BA0] mb-4">
            Email d'activation envoyé (SMTP Gmail actif). Sinon, lien dev affiché.
          </p>

          {/* Role tabs */}
          <div className="flex gap-2 mb-5" data-testid="invite-role-tabs">
            {[
              { id: "licencie", label: "Licencié" },
              { id: "admin", label: "Admin (collab.)" },
            ].map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRole(r.id)}
                data-testid={`invite-role-${r.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  role === r.id
                    ? "bg-[#191D1E] text-white border-[#191D1E]"
                    : "bg-white text-[#687175] border-[#E5E7EB] hover:bg-[#F5F6F9]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="invite-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label htmlFor="i-prenom">Prénom</Label><Input id="i-prenom" data-testid="invite-prenom" value={form.prenom} onChange={onChange("prenom")} required /></div>
              <div className="space-y-1.5"><Label htmlFor="i-nom">Nom</Label><Input id="i-nom" data-testid="invite-nom" value={form.nom} onChange={onChange("nom")} required /></div>
            </div>
            <div className="space-y-1.5"><Label htmlFor="i-email">Email</Label><Input id="i-email" data-testid="invite-email" type="email" value={form.email} onChange={onChange("email")} required /></div>
            {role === "licencie" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="i-zone">Zone / Agence PDE</Label>
                  {!zoneCustom ? (
                    <select
                      id="i-zone"
                      data-testid="invite-zone"
                      value={form.zone}
                      onChange={(e) => {
                        if (e.target.value === "__other__") { setZoneCustom(true); setForm((f) => ({ ...f, zone: "" })); }
                        else setForm((f) => ({ ...f, zone: e.target.value }));
                      }}
                      required
                      className="flex h-10 w-full rounded-xl border-[1.5px] border-[#BFC6CC] bg-white px-3 py-2 text-sm text-[#191D1E] focus:border-[#397DFF] focus:outline-none focus:ring-4 focus:ring-[#397DFF]/12"
                    >
                      <option value="">Sélectionner une zone…</option>
                      {agencies.map((g) => (
                        <optgroup key={g.region} label={g.region}>
                          {g.zones.map((z) => <option key={z} value={z}>{z}</option>)}
                        </optgroup>
                      ))}
                      <option value="__other__">— Autre zone (saisie libre)</option>
                    </select>
                  ) : (
                    <div className="flex gap-2">
                      <Input id="i-zone" data-testid="invite-zone" placeholder="Saisissez la zone" value={form.zone} onChange={onChange("zone")} required />
                      <Button type="button" variant="outline" onClick={() => { setZoneCustom(false); setForm((f) => ({ ...f, zone: "" })); }} className="rounded-xl">Liste</Button>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5"><Label htmlFor="i-resp">Responsable</Label><Input id="i-resp" data-testid="invite-responsable" value={form.responsable} onChange={onChange("responsable")} required /></div>
              </>
            )}
            {role === "admin" && (
              <div className="text-xs text-[#687175] bg-[#F5F6F9] border border-[#E5E7EB] rounded-lg p-3 leading-relaxed">
                Cet utilisateur disposera des <strong>droits d'administrateur·trice</strong> sur la plateforme : leads, commerçants, licenciés, kit marketing, statistiques.
              </div>
            )}
            {error && <div data-testid="invite-error" className="px-3 py-2 rounded-lg bg-[#FEF2F2] text-[#B91C1C] text-sm border border-[#FECACA]">{error}</div>}
            <Button type="submit" data-testid="invite-submit" disabled={submitting} className="w-full h-11 bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl shadow-[0_4px_14px_rgba(249,200,14,0.32)]">
              {submitting ? "Envoi..." : (role === "admin" ? "Inviter ce·tte collaborateur·trice" : "Envoyer l'invitation")}
            </Button>
          </form>

          {lastDevLink && (
            <div className="mt-5 rounded-xl bg-[#FFF7E6] border border-[#FDE68A] p-4" data-testid="last-dev-link">
              <div className="flex items-center gap-2 text-[#92400E] text-xs uppercase tracking-wider font-semibold mb-2">
                <Mail className="w-3.5 h-3.5" /> Mode dev — lien à transmettre
              </div>
              <div className="text-xs text-[#191D1E] break-all bg-white rounded-lg p-2 border border-[#FDE68A]">{lastDevLink}</div>
              <Button type="button" onClick={() => copyLink(lastDevLink)} data-testid="copy-last-link" className="mt-2 h-9 bg-[#397DFF] hover:bg-[#2860D8] text-white text-sm rounded-lg"><Copy className="w-3.5 h-3.5 mr-1.5" /> Copier</Button>
            </div>
          )}
        </div>
      </section>

      <section className="lg:col-span-3">
        <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(57,125,255,0.06)] p-6 sm:p-7">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <h2 className="font-display text-2xl font-bold text-[#191D1E]">Invitations envoyées</h2>
            <Button variant="outline" onClick={reload} data-testid="invitations-refresh" className="rounded-xl border-[#E5E7EB] hover:bg-[#F5F6F9]">
              <RefreshCw className={`w-4 h-4 mr-2 ${listLoading ? "animate-spin" : ""}`} /> Actualiser
            </Button>
          </div>
          <div className="text-xs text-[#687175] mb-4">
            <strong className="text-[#9D174D]">{counts.admin}</strong> admin · <strong className="text-[#3730A3]">{counts.licencie}</strong> licenciés · <strong className="text-[#065F46]">{counts.commercant}</strong> commerçants
          </div>
          <div className="flex gap-2 mb-4 flex-wrap" data-testid="invitations-filter">
            {ROLE_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                data-testid={`invite-filter-${f.value}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${filter === f.value ? "bg-[#397DFF] text-white border-[#397DFF]" : "bg-white text-[#687175] border-[#E5E7EB]"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {!listLoading && visibleInvitations.length === 0 && (
            <div className="text-sm text-[#939BA0] py-10 text-center" data-testid="invitations-empty">Aucune invitation avec ce filtre.</div>
          )}
          <ul className="divide-y divide-[#E5E7EB]" data-testid="invitations-list">
            {visibleInvitations.map((inv) => {
              const badge = STATUS_BADGE[inv.status] || STATUS_BADGE.pending;
              const roleB = ROLE_BADGE[inv.role] || ROLE_BADGE.licencie;
              return (
                <li key={inv.id} className="py-4 flex items-start justify-between gap-4" data-testid={`invitation-row-${inv.email}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#191D1E]">{inv.prenom} {inv.nom}</span>
                      <Badge className={`${roleB.className} border text-[10px]`}>{roleB.label}</Badge>
                      <Badge className={`${badge.className} border`}>{badge.label}</Badge>
                    </div>
                    <div className="text-sm text-[#687175] mt-0.5">{inv.email}</div>
                    {inv.role === "licencie" && (
                      <div className="text-xs text-[#939BA0] mt-1">
                        Zone : <span className="text-[#191D1E]">{inv.zone || "—"}</span>
                        {inv.responsable && <> · Resp. : <span className="text-[#191D1E]">{inv.responsable}</span></>}
                      </div>
                    )}
                    {inv.dev_link && (
                      <div className="mt-2 flex items-center gap-2">
                        <code className="text-[11px] text-[#397DFF] bg-[#F5F6F9] px-2 py-1 rounded-lg truncate max-w-[280px] sm:max-w-[420px]">{inv.dev_link}</code>
                        <Button type="button" onClick={() => copyLink(inv.dev_link)} data-testid={`copy-link-${inv.email}`} className="h-7 px-2 bg-[#397DFF] hover:bg-[#2860D8] text-white text-xs rounded-lg"><Copy className="w-3 h-3 mr-1" /> Copier</Button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    </div>
  );
}

export default InvitationsPanel;
